import { apiService } from './api';

export enum QuestionType {
  MCQ = 'MCQ',
  FILL_IN_THE_BLANKS = 'Fill in the blanks',
  SHORT = 'Short',
  LONG = 'Long'
}

export enum QuestionImportance {
  HIGH = 'High',
  MEDIUM = 'Medium',
  LOW = 'Low'
}

export enum QuestionDifficulty {
  EASY = 'Easy',
  MEDIUM = 'Medium',
  HARD = 'Hard'
}

export interface QuestionMetadata {
  tags?: string[];
  pastPaperReferences?: string[];
  marks?: number;
  difficulty?: string;
  estimatedTime?: number;
  [key: string]: any;
}

export interface SubPart {
  text: string;
}

export interface Question {
  _id: string;
  chapterId: string | { _id: string; name: string };
  questionText: string;
  type: QuestionType;
  importance: QuestionImportance;
  options?: string[];
  correctAnswer?: string;
  answer?: string;
  answerImages?: string[];
  questionImages?: string[];
  subParts?: SubPart[];
  metadata?: QuestionMetadata;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Chapter {
  _id: string;
  questionBankId: string;
  name: string;
  description?: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  questionCount?: number;
}

export interface QuestionBank {
  _id: string;
  schoolId: string | { _id: string; name: string };
  classId: string | { _id: string; name: string };
  subjectId: string | { _id: string; name: string };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Server-computed counts. Returned by the list endpoint, so the FE doesn't
  // need to fan out a per-bank fetch just to show "X questions / Y chapters".
  chapterCount?: number;
  totalQuestionCount?: number;
}

export interface QuestionBankFormData {
  classId: string;
  subjectId: string;
}

export interface ChapterFormData {
  questionBankId: string;
  name: string;
  description?: string;
  order?: number;
}

export interface QuestionFormData {
  chapterId: string;
  questionText: string;
  type: QuestionType;
  importance?: QuestionImportance;
  options?: string[];
  correctAnswer?: string;
  answer?: string;
  // Bare filenames of images attached to the sample answer. Uploaded via
  // `uploadAnswerImages` — the server writes them to `uploads/answer-images/`
  // and returns filenames that we plug into this array on save.
  answerImages?: string[];
  // Bare filenames of images attached to the question stem itself. Applies
  // to every question type. Uploaded via `uploadQuestionImages`.
  questionImages?: string[];
  subParts?: SubPart[];
  metadata?: QuestionMetadata;
}

export const questionPaperService = {
  // ==================== Question Bank Operations ====================

  // Get all question banks
  getQuestionBanks: async (params?: {
    schoolId?: string;
    classId?: string;
    subjectId?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiService.get<{ questionBanks: QuestionBank[], pagination: any }>(
      '/question-paper/question-banks',
      params
    );
    return {
      questionBanks: response.data?.questionBanks || [],
      pagination: response.data?.pagination || { totalItems: 0, totalPages: 1, currentPage: 1 }
    };
  },

  // Get question bank by ID
  getQuestionBank: async (id: string) => {
    const response = await apiService.get<QuestionBank>(`/question-paper/question-banks/${id}`);
    return response.data;
  },

  // Create question bank
  createQuestionBank: async (data: QuestionBankFormData) => {
    const response = await apiService.post<QuestionBank>('/question-paper/question-banks', data);
    return response.data;
  },

  // Delete question bank
  deleteQuestionBank: async (id: string) => {
    const response = await apiService.delete(`/question-paper/question-banks/${id}`);
    return response.data;
  },

  // ==================== Chapter Operations ====================

  // Get chapters for a question bank
  getChapters: async (questionBankId: string) => {
    const response = await apiService.get<Chapter[]>(
      `/question-paper/question-banks/${questionBankId}/chapters`
    );
    return response.data || [];
  },

  // Get chapters by class and subject
  getChaptersByClassAndSubject: async (classId: string, subjectId: string) => {
    const response = await apiService.get<Chapter[]>(
      '/question-paper/chapters',
      { classId, subjectId }
    );
    return response.data || [];
  },

  // Create chapter
  createChapter: async (data: ChapterFormData) => {
    const response = await apiService.post<Chapter>('/question-paper/chapters', data);
    return response.data;
  },

  // Update chapter
  updateChapter: async (id: string, data: Partial<ChapterFormData>) => {
    const response = await apiService.put<Chapter>(`/question-paper/chapters/${id}`, data);
    return response.data;
  },

  // Delete chapter
  deleteChapter: async (id: string) => {
    const response = await apiService.delete(`/question-paper/chapters/${id}`);
    return response.data;
  },

  // ==================== Question Operations ====================

  // Get questions for a chapter
  getQuestions: async (chapterId: string, params?: {
    type?: string;
    importance?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiService.get<{ questions: Question[], pagination: any }>(
      `/question-paper/chapters/${chapterId}/questions`,
      params
    );
    return {
      questions: response.data?.questions || [],
      pagination: response.data?.pagination || { totalItems: 0, totalPages: 1, currentPage: 1 }
    };
  },

  // Bank-wide question search — same response shape as getQuestions so the
  // page can swap call sites without remapping. Uses the text index on the
  // server, so this is much faster than regex once a bank grows past a few
  // thousand questions. Optional chapterId narrows it to one chapter.
  getQuestionsByBank: async (questionBankId: string, params?: {
    chapterId?: string;
    type?: string;
    importance?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await apiService.get<{ questions: Question[], pagination: any }>(
      `/question-paper/question-banks/${questionBankId}/questions`,
      params
    );
    return {
      questions: response.data?.questions || [],
      pagination: response.data?.pagination || { totalItems: 0, totalPages: 1, currentPage: 1 }
    };
  },

  // Get question by ID
  getQuestion: async (id: string) => {
    const response = await apiService.get<Question>(`/question-paper/questions/${id}`);
    return response.data;
  },

  // Create question
  createQuestion: async (data: QuestionFormData) => {
    const response = await apiService.post<Question>('/question-paper/questions', data);
    return response.data;
  },

  // Update question
  updateQuestion: async (id: string, data: Partial<QuestionFormData>) => {
    const response = await apiService.put<Question>(`/question-paper/questions/${id}`, data);
    return response.data;
  },

  // Delete question
  deleteQuestion: async (id: string) => {
    const response = await apiService.delete(`/question-paper/questions/${id}`);
    return response.data;
  },

  // ==================== CSV Operations ====================

  // Download sample CSV
  downloadSampleCSV: async () => {
    await apiService.download('/question-paper/sample-csv', 'question_bank_sample.csv');
  },

  // Upload questions from CSV
  uploadQuestionsCSV: async (chapterId: string, file: File) => {
    const formData = new FormData();
    formData.append('csv', file);

    const response = await apiService.upload<any>(
      `/question-paper/chapters/${chapterId}/upload-csv`,
      formData
    );

    return response.data;
  },

  // Upload one or more sample-answer image attachments. Returns the stored
  // filenames — the caller appends them to `QuestionFormData.answerImages`
  // before submitting the create/update.
  uploadAnswerImages: async (files: File[]): Promise<string[]> => {
    if (files.length === 0) return [];
    const formData = new FormData();
    for (const f of files) formData.append('answerImage', f);
    const response = await apiService.upload<{ filenames: string[] }>(
      '/question-paper/questions/answer-images',
      formData
    );
    return response.data?.filenames || [];
  },

  // Same as above, but for question-stem image attachments.
  uploadQuestionImages: async (files: File[]): Promise<string[]> => {
    if (files.length === 0) return [];
    const formData = new FormData();
    for (const f of files) formData.append('questionImage', f);
    const response = await apiService.upload<{ filenames: string[] }>(
      '/question-paper/questions/question-images',
      formData
    );
    return response.data?.filenames || [];
  }
};

